import { DayType, MonthSubMode } from '@griply/types';
import {
  getMonthDayOrdinals,
  getWeekDays,
  MONTH_DAYS,
  MONTH_ORDINALS,
  parseMonthDaysFromRrule,
  parseMonthWeekDayFromRrule,
  setMonthDaysInRrule,
  setMonthSpecificDayInRrule,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'shared/components/ui/form-select';
import { RadioInput } from 'shared/components/ui/radio-input';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './form-body.style';

const END_OF_MONTH_DAYS = [29, 30, 31];

export type MonthsFormProps = {
  rrule: string;
  onChange: (rrule: string) => void;
  weekStartsOn: WeekDays;
};

export const RruleMonthsBody: React.FC<MonthsFormProps> = ({
  rrule,
  weekStartsOn,
  onChange,
}) => {
  const { t } = useTranslation();
  const selectedMonthDays = useMemo(
    () => parseMonthDaysFromRrule(rrule),
    [rrule],
  );
  const selectedWeekDay = useMemo(
    () => parseMonthWeekDayFromRrule(rrule),
    [rrule],
  );
  const initialOrdinalWeekDay = useMemo(
    () => getWeekDays(weekStartsOn)[0],
    [weekStartsOn],
  );

  const onChangeOption = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.value === MonthSubMode.SpecificDay) {
      return onChange(
        setMonthSpecificDayInRrule(rrule, {
          weekDay: initialOrdinalWeekDay,
          ordinal: MONTH_ORDINALS[0],
        }),
      );
    }

    onChange(setMonthDaysInRrule(rrule, []));
  };

  const onChangeMonthDay = (e: React.MouseEvent) => {
    e.preventDefault();
    const dayValue = e.currentTarget.getAttribute('data-value');
    const dayNumber = parseInt(dayValue ?? '');

    const newSelectedMonthDays = selectedMonthDays.filter(
      (monthDay) => monthDay !== dayNumber,
    );
    if (newSelectedMonthDays.length === selectedMonthDays.length) {
      newSelectedMonthDays.push(dayNumber);
    }

    onChange(setMonthDaysInRrule(rrule, newSelectedMonthDays));
  };

  const onChangeOrdinalDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setMonthSpecificDayInRrule(rrule, {
        weekDay: e.target.value as DayType | WeekDays,
        ordinal: selectedWeekDay.ordinal,
      }),
    );
  };

  const onChangeOrdinalNumber = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setMonthSpecificDayInRrule(rrule, {
        weekDay: selectedWeekDay.weekDay,
        ordinal: parseInt(e.target.value),
      }),
    );
  };

  const ordinalOptions = useMemo(
    () =>
      MONTH_ORDINALS.map((ordinal) => ({
        label: t(
          `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label`,
        ),
        value: ordinal,
      })),
    [t],
  );

  const ordinalDayOptions = useMemo(
    () =>
      getMonthDayOrdinals(weekStartsOn).map((weekDay) => ({
        label: t(
          `general.labels.habit.repeat.specific-days.days.${weekDay}.label`,
        ),
        value: weekDay,
      })),
    [t, weekStartsOn],
  );

  const monthDayWarning = useMemo(() => {
    const lastSelectedDays = END_OF_MONTH_DAYS.filter((day) =>
      selectedMonthDays.includes(day),
    );

    if (!lastSelectedDays.length) {
      return;
    }

    if (lastSelectedDays.length > 1) {
      const lastEntry = lastSelectedDays[lastSelectedDays.length - 1];
      const firstEntries = lastSelectedDays.slice(0, -1);

      return t(
        'forms.habit.fields.repeat.months.specific-days.last-day.label',
        {
          days: [
            firstEntries
              .map((day) => t(`general.labels.${day}.ordinal`))
              .join(', '),
            t('general.labels.and'),
            t(`general.labels.${lastEntry}.ordinal`),
          ].join(' '),
          amount: lastEntry,
          count: lastSelectedDays.length,
        },
      );
    }

    return t('forms.habit.fields.repeat.months.specific-days.last-day.label', {
      days: t(`general.labels.${lastSelectedDays[0]}.ordinal`),
      amount: lastSelectedDays[0],
      count: 1,
    });
  }, [selectedMonthDays, t]);

  return (
    <Styled.Container>
      {/* Specific day */}
      <Styled.RadioContainer>
        <RadioInput
          id={MonthSubMode.SpecificDays}
          name="month-options"
          checked={!selectedWeekDay}
          onChange={onChangeOption}
          value={MonthSubMode.SpecificDays}
        >
          {t('forms.habit.fields.repeat.months.specific-days.label')}
        </RadioInput>

        <Styled.RadioBody $disabled={!!selectedWeekDay}>
          <Styled.MonthDayList>
            {MONTH_DAYS.map((monthDay) => (
              <Styled.MonthDayListItem key={monthDay}>
                <Styled.DayButton
                  onClick={onChangeMonthDay}
                  data-value={monthDay}
                  $selected={selectedMonthDays.includes(monthDay)}
                  disabled={!!selectedWeekDay}
                  type="button"
                >
                  {monthDay}
                </Styled.DayButton>
              </Styled.MonthDayListItem>
            ))}
          </Styled.MonthDayList>
          {!!monthDayWarning && (
            <Styled.MonthDayWarning>{monthDayWarning}</Styled.MonthDayWarning>
          )}
        </Styled.RadioBody>
      </Styled.RadioContainer>

      {/* based-on-month day */}
      <Styled.RadioContainer>
        <RadioInput
          id={MonthSubMode.SpecificDay}
          name="month-options"
          checked={!!selectedWeekDay}
          onChange={onChangeOption}
          value={MonthSubMode.SpecificDay}
        >
          {t('forms.habit.fields.repeat.months.specific-day.label')}
        </RadioInput>

        <Styled.RadioBody $disabled={false}>
          <Styled.TimesPerContainer>
            <FormSelect
              value={selectedWeekDay?.ordinal}
              options={ordinalOptions}
              onChange={onChangeOrdinalNumber}
              disabled={!selectedWeekDay}
              isSmall
            />

            <FormSelect
              value={selectedWeekDay?.weekDay}
              options={ordinalDayOptions}
              onChange={onChangeOrdinalDay}
              disabled={!selectedWeekDay}
              isSmall
            />
          </Styled.TimesPerContainer>
        </Styled.RadioBody>
      </Styled.RadioContainer>
    </Styled.Container>
  );
};
